import {
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
} from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import type { ComponentPropsWithoutRef } from "react";

// Tiny className joiner — avoids pulling in a "cn"/clsx dependency that
// isn't used anywhere else in this app.
const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(" ");

// Firefox renders the password-dot glyph narrower than Chromium does, so
// the stand-in character used for measuring caret width differs slightly
// per engine to keep the estimate accurate.
const PASSWORD_CHAR =
  typeof navigator !== "undefined" &&
  navigator.userAgent.match(/firefox|fxios/i)
    ? "\u25CF"
    : "\u2022";

export type SmoothInputProps = ComponentPropsWithoutRef<"input"> & {
  wrapperClassName?: string;
};

// Drop-in replacement for a plain <input>: same props, same className goes
// on the actual input, but the native caret is hidden and replaced with a
// spring-animated overlay that glides to each new cursor position (typing,
// clicking, arrow keys, selection changes). Canvas/span text measurement
// keeps it aligned with the real text, including through password masking.
//
// Used everywhere the app has a free-text field — seller dashboard, seller
// flow forms, search bars — so typing feels the same across the whole app.
export const SmoothInput = ({
  className,
  wrapperClassName,
  value,
  defaultValue,
  onChange,
  onBlur,
  type = "text",
  style,
  ...props
}: SmoothInputProps) => {
  const [internalValue, setInternalValue] = useState(defaultValue ?? "");
  const [caretColor, setCaretColor] = useState("currentColor");
  const caretX = useMotionValue(0);
  // caretOpacity handles VISIBILITY (focused + in view vs. blurred/scrolled
  // off/selection active). blinkOpacity layers the actual on/off pulse on
  // top of that, the way a native caret blinks.
  const caretOpacity = useMotionValue(0);
  const blinkOpacity = useMotionValue(1);
  const blinkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blinkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const prefersReducedMotion = useReducedMotion();

  const clearBlinkTimers = () => {
    if (blinkTimeoutRef.current) {
      clearTimeout(blinkTimeoutRef.current);
      blinkTimeoutRef.current = null;
    }
    if (blinkIntervalRef.current) {
      clearInterval(blinkIntervalRef.current);
      blinkIntervalRef.current = null;
    }
  };

  // Snap the caret solid immediately (matches native behavior whenever you
  // type or move the cursor), then resume the on/off blink after a short
  // pause of inactivity — rather than blinking continuously regardless of
  // activity, which reads as jittery while actively typing.
  const restartBlink = () => {
    clearBlinkTimers();
    blinkOpacity.set(1);

    if (prefersReducedMotion) return;

    blinkTimeoutRef.current = setTimeout(() => {
      blinkIntervalRef.current = setInterval(() => {
        blinkOpacity.set(blinkOpacity.get() > 0 ? 0 : 1);
      }, 530);
    }, 500);
  };

  useEffect(() => clearBlinkTimers, []);

  const isControlled = value !== undefined;
  const inputValue = isControlled ? String(value) : internalValue;

  const springCaretX = useSpring(
    caretX,
    prefersReducedMotion
      ? { stiffness: 10000, damping: 100, mass: 0.1 }
      : { stiffness: 500, damping: 30, mass: 0.5 }
  );

  const syncMeasureSpan = () => {
    const input = inputRef.current;
    const measureSpan = measureRef.current;
    if (!input || !measureSpan) return;

    const styles = window.getComputedStyle(input);
    const isPassword = input.type === "password";

    let fontSize = styles.fontSize;
    if (
      PASSWORD_CHAR === "\u2022" &&
      isPassword &&
      !navigator.userAgent.match(/chrome|chromium|crios/i)
    ) {
      fontSize = `${parseFloat(fontSize) + 6.25}px`;
    }

    measureSpan.style.font = `${styles.fontStyle} ${styles.fontWeight} ${fontSize} ${styles.fontFamily}`;
    measureSpan.style.letterSpacing = styles.letterSpacing;
    measureSpan.style.fontFeatureSettings = styles.fontFeatureSettings;
    measureSpan.style.fontVariationSettings = styles.fontVariationSettings;
    // Match whatever color the input itself resolves to (Tailwind class,
    // inherited, or inline) rather than hard-coding a theme token — this
    // app doesn't use shadcn's CSS variables.
    setCaretColor(styles.color);
  };

  const measurePrefixWidth = (text: string) => {
    const input = inputRef.current;
    const measureSpan = measureRef.current;
    if (!input || !measureSpan) return null;

    syncMeasureSpan();
    measureSpan.textContent = text;

    const paddingLeft =
      parseFloat(window.getComputedStyle(input).paddingLeft) || 0;

    return text.length > 0
      ? measureSpan.offsetWidth + paddingLeft
      : paddingLeft - 1;
  };

  const scrollCaretIntoView = (
    target: HTMLInputElement,
    absoluteWidth: number
  ) => {
    const styles = window.getComputedStyle(target);
    const paddingLeft = parseFloat(styles.paddingLeft) || 0;
    const paddingRight = parseFloat(styles.paddingRight) || 0;
    const maxScroll = Math.max(0, target.scrollWidth - target.clientWidth);
    const visibleRight = target.scrollLeft + target.clientWidth - paddingRight;
    const visibleLeft = target.scrollLeft + paddingLeft;

    if (absoluteWidth > visibleRight) {
      target.scrollLeft = Math.min(
        absoluteWidth - target.clientWidth + paddingRight,
        maxScroll
      );
      return;
    }

    if (absoluteWidth < visibleLeft) {
      target.scrollLeft = Math.max(0, absoluteWidth - paddingLeft);
    }
  };

  const getCaretIndex = (target: HTMLInputElement) => {
    const selectionStart = target.selectionStart ?? 0;
    const selectionEnd = target.selectionEnd ?? 0;

    if (selectionStart === selectionEnd) {
      return selectionStart;
    }

    return target.selectionDirection === "backward"
      ? selectionStart
      : selectionEnd;
  };

  const updateCaretFromInput = (target: HTMLInputElement) => {
    const selectionStart = target.selectionStart ?? 0;
    const selectionEnd = target.selectionEnd ?? 0;
    const hasSelection = selectionStart !== selectionEnd;
    const caretIndex = getCaretIndex(target);
    const isPassword = target.type === "password";
    const textBeforeCaret = isPassword
      ? PASSWORD_CHAR.repeat(caretIndex)
      : target.value.slice(0, caretIndex);

    const absoluteWidth = measurePrefixWidth(textBeforeCaret);
    if (absoluteWidth === null) return;

    scrollCaretIntoView(target, absoluteWidth);

    const styles = window.getComputedStyle(target);
    const paddingLeft = parseFloat(styles.paddingLeft) || 0;
    const paddingRight = parseFloat(styles.paddingRight) || 0;
    const caretPosition = absoluteWidth - target.scrollLeft;
    const minX = paddingLeft - 1;
    const maxX = target.clientWidth - paddingRight;
    const isCaretVisible = caretPosition >= minX && caretPosition <= maxX + 1;

    caretX.set(Math.min(caretPosition, maxX));

    if (!isCaretVisible || hasSelection) {
      caretOpacity.set(0);
      clearBlinkTimers();
      return;
    }

    caretOpacity.set(1);
    restartBlink();
  };

  const updateCaretRef = useRef(updateCaretFromInput);
  updateCaretRef.current = updateCaretFromInput;
  const caretOpacityRef = useRef(caretOpacity);
  caretOpacityRef.current = caretOpacity;

  useEffect(() => {
    const input = inputRef.current;
    if (input && document.activeElement === input) {
      updateCaretRef.current(input);
    }
  }, [inputValue]);

  useEffect(() => {
    const input = inputRef.current;
    const container = containerRef.current;
    if (!input || !container) return;

    const updateCaretIfFocused = () => {
      if (document.activeElement === input) {
        updateCaretRef.current(input);
      }
    };

    const handleSelectionChange = () => {
      if (document.activeElement !== input) return;

      requestAnimationFrame(() => {
        if (document.activeElement === input) {
          updateCaretRef.current(input);
        }
      });
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    document.fonts.addEventListener("loadingdone", updateCaretIfFocused);
    void document.fonts.ready.then(updateCaretIfFocused);
    input.addEventListener("scroll", updateCaretIfFocused);

    const resizeObserver = new ResizeObserver(updateCaretIfFocused);
    resizeObserver.observe(container);

    updateCaretIfFocused();

    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
      document.fonts.removeEventListener("loadingdone", updateCaretIfFocused);
      input.removeEventListener("scroll", updateCaretIfFocused);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn("relative grid grid-cols-1", wrapperClassName)}
    >
      <input
        {...props}
        ref={inputRef}
        type={type}
        className={cn("col-start-1 col-end-2 row-start-1 row-end-2", className)}
        style={{ ...style, caretColor: "transparent" }}
        value={inputValue}
        onChange={(e) => {
          if (!isControlled) setInternalValue(e.target.value);
          onChange?.(e);
          requestAnimationFrame(() => {
            updateCaretRef.current(e.target);
          });
        }}
        onBlur={(e) => {
          caretOpacityRef.current.set(0);
          clearBlinkTimers();
          onBlur?.(e);
        }}
      />
      <span
        ref={measureRef}
        aria-hidden
        className="pointer-events-none invisible absolute top-0 left-0 whitespace-pre"
      />
      <motion.div
        className="pointer-events-none col-start-1 col-end-2 row-start-1 row-end-2 h-[0.9em] w-0.5 self-center"
        style={{ x: springCaretX, opacity: caretOpacity }}
      >
        <motion.div
          className="h-full w-full"
          style={{ opacity: blinkOpacity, backgroundColor: caretColor }}
        />
      </motion.div>
    </div>
  );
};

export default SmoothInput;
